import { useState } from 'react';
import { CoverageKey, IInsightsFilter, isFiltered } from '@innerhive-internal/innerhive-common';

export interface IInsightsNarrowing {
    filter: IInsightsFilter;
    narrowed: boolean;
    /** Which coverage meter is open. A disclosure, not part of the narrowing. */
    openMeter?: CoverageKey;
    toggleKind: (kind: IInsightsFilter['kind']) => void;
    toggleReading: (reading: IInsightsFilter['reading']) => void;
    /** Narrows to the reading whether or not the map already is, for a control that only ever narrows. */
    narrowToReading: (reading: IInsightsFilter['reading']) => void;
    toggleMeter: (key: CoverageKey) => void;
    clear: () => void;
}

/**
 * What the reader has narrowed the caseload map to, and the controls that set it.
 *
 * Every toggle clears on a second press, so each control is its own way back out and the reader
 * never has to hunt for Clear to undo the click they just made.
 *
 * The open coverage meter rides along here because it is set from the meters, but it is deliberately
 * not part of the filter: coverage is a property of the client rather
 * than of a finding, so it does not narrow the diagrams.
 */
export const useInsightsFilter = (): IInsightsNarrowing => {
    const [filter, setFilter] = useState<IInsightsFilter>({});
    const [openMeter, setOpenMeter] = useState<CoverageKey | undefined>();

    return {
        filter,
        narrowed: isFiltered(filter),
        openMeter,
        toggleKind: kind => setFilter(current => ({ ...current, kind: current.kind === kind ? undefined : kind })),
        toggleReading: reading => setFilter(current => ({
            ...current,
            reading: current.reading === reading ? undefined : reading
        })),
        narrowToReading: reading => setFilter(current => ({ ...current, reading })),
        toggleMeter: key => setOpenMeter(current => (current === key ? undefined : key)),
        clear: () => setFilter({})
    };
};
